import routes, { composeRoute } from 'constants/routes';
import { UserPermission } from 'constants/userPermission';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons/faShareNodes';
import { faFolder } from '@fortawesome/pro-regular-svg-icons/faFolder';
import { faUserGroup } from '@fortawesome/pro-regular-svg-icons/faUserGroup';
import { faBuilding } from '@fortawesome/pro-regular-svg-icons/faBuilding';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { faArrowRightToArc } from '@fortawesome/pro-regular-svg-icons/faArrowRightToArc';
import { faShieldHalved } from '@fortawesome/pro-regular-svg-icons/faShieldHalved';
import { faBell } from '@fortawesome/pro-regular-svg-icons/faBell';
import { faMobileScreenButton } from '@fortawesome/pro-regular-svg-icons/faMobileScreenButton';
import { faCreditCard } from '@fortawesome/pro-regular-svg-icons/faCreditCard';
import { faLanguage } from '@fortawesome/pro-regular-svg-icons/faLanguage';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { useUser } from 'modules/user/userSelector';
import { useUserPermissions } from 'app/hooks/useUserPermissions';

export type SettingsSidebarItem = {
  key: string;
  label: string;
  icon: IconDefinition;
  to: string;
  isExternal?: boolean;
  permission?: UserPermission | UserPermission[];
  roles?: string[];
  type?: 'language';
};

export type SettingsSidebarGroup = {
  key: string;
  label: string;
  items: SettingsSidebarItem[];
};

// Every listed permission is required, like legacy Permissions::userCanAccess*().
export const SETTINGS_SECTION_PERMISSIONS = {
  PROFILES: [UserPermission.ACCESS_PROFILES],
  PROFILE_GROUPS: [
    UserPermission.ACCESS_PROFILE_GROUPS,
    UserPermission.PAGE_PRESETS_MANAGE,
  ],
  GLOBAL_PROFILES: [
    UserPermission.ACCESS_PROFILES,
    UserPermission.ACCESS_GLOBAL_PAGES,
    UserPermission.PAGE_EDIT,
  ],
  USERS: [UserPermission.ACCESS_USERS],
  COMPANY_SETTINGS: [UserPermission.ACCESS_COMPANY_SETTINGS],
} satisfies Record<string, UserPermission[]>;

const SETTINGS_V2_ROUTES = {
  COMPANY_SETTINGS: routes.SETTINGS.COMPANY,
  PREFERENCES: composeRoute('settings.routes.notifications.path'),
  DEVICES: '/settings/devices',
};

export const useSettingsSidebarItems = (): SettingsSidebarGroup[] => {
  const { isTrial } = useSubscriptionInfo();
  const { role } = useUser();
  const { hasEvery } = useUserPermissions();

  const channelsItems: (SettingsSidebarItem | false)[] = [
    {
      key: 'profiles',
      label: 'settings.sidebar.profiles',
      icon: faShareNodes,
      to: routes.SETTINGS.PROFILES,
      permission: SETTINGS_SECTION_PERMISSIONS.PROFILES,
    },
    {
      key: 'profile-groups',
      label: 'settings.sidebar.profileGroups',
      icon: faFolder,
      to: routes.SETTINGS.PROFILE_GROUPS,
      permission: SETTINGS_SECTION_PERMISSIONS.PROFILE_GROUPS,
    },
    {
      key: 'kontentbase',
      label: 'settings.sidebar.kontentbase',
      icon: faGlobe,
      to: routes.SETTINGS.GLOBAL_PROFILES,
      permission: SETTINGS_SECTION_PERMISSIONS.GLOBAL_PROFILES,
    },
  ];

  const workspaceItems: (SettingsSidebarItem | false)[] = [
    {
      key: 'users',
      label: 'settings.sidebar.users',
      icon: faUserGroup,
      to: routes.SETTINGS.USERS,
      permission: SETTINGS_SECTION_PERMISSIONS.USERS,
    },
    {
      key: 'company-settings',
      label: 'settings.sidebar.companySettings',
      icon: faBuilding,
      to: routes.SETTINGS.COMPANY,
      permission: SETTINGS_SECTION_PERMISSIONS.COMPANY_SETTINGS,
    },
    {
      key: 'language',
      label: 'language',
      icon: faLanguage,
      to: '',
      type: 'language',
    },
  ];

  const meItems: (SettingsSidebarItem | false)[] = [
    {
      key: 'personal',
      label: 'settings.sidebar.myAccount',
      icon: faUser,
      to: routes.SETTINGS.PERSONAL,
    },
    {
      key: 'authorization',
      label: 'settings.sidebar.authorization',
      icon: faArrowRightToArc,
      to: routes.SETTINGS.AUTHORIZATION,
      roles: ['admin', 'manager'],
    },
    {
      key: 'preferences',
      label: 'settings.sidebar.preferences',
      icon: faBell,
      to: SETTINGS_V2_ROUTES.PREFERENCES,
    },
    {
      key: 'my-devices',
      label: 'settings.sidebar.myDevices',
      icon: faMobileScreenButton,
      to: SETTINGS_V2_ROUTES.DEVICES,
    },
    {
      key: 'security',
      label: 'settings.sidebar.security',
      icon: faShieldHalved,
      to: routes.SETTINGS.SECURITY,
    },
  ];

  const billingItems: (SettingsSidebarItem | false)[] = [
    {
      key: 'billing-dashboard',
      label: 'settings.sidebar.billingDashboard',
      icon: faCreditCard,
      to: routes.SETTINGS.BILLING,
      permission: UserPermission.ACCESS_BILLING,
      roles: ['admin'],
    },
  ];

  const filterItems = (
    items: (SettingsSidebarItem | false)[],
  ): SettingsSidebarItem[] =>
    items
      .filter((item): item is SettingsSidebarItem => Boolean(item))
      .filter((item) => !item.roles || item.roles.includes(role))
      .filter((item) => !item.permission || hasEvery(item.permission));

  const groups: SettingsSidebarGroup[] = [
    {
      key: 'channels',
      label: 'settings.sidebar.channels',
      items: filterItems(channelsItems),
    },
    {
      key: 'workspace',
      label: 'settings.sidebar.workspace',
      items: filterItems(workspaceItems),
    },
    {
      key: 'me',
      label: 'settings.sidebar.me',
      items: filterItems(meItems),
    },
  ].filter((group) => group.items.length > 0);

  // Billing is blocked for trial accounts; hide only that group, not Settings.
  if (!isTrial) {
    const billingFiltered = filterItems(billingItems);
    if (billingFiltered.length > 0) {
      groups.push({
        key: 'billing',
        label: 'settings.sidebar.billing',
        items: billingFiltered,
      });
    }
  }

  return groups;
};
